import type { Attachment } from '@medplum/fhirtypes'
import { Button } from '@seen/ui'
import { IconRefresh } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRetryAttachmentConversion } from '#src/hooks/useRetryAttachmentConversion.ts'

interface RetryConversionButtonProps {
  attachment: Attachment
  documentReferenceId: string | undefined
  size?: 'sm' | 'default'
}

/**
 * Retry button surfaced inside the failed-state placeholder for an attachment
 * whose server-side conversion (e.g. HEIC → JPEG) gave up. Flips the
 * `attachment-conversion: status` extension back to `pending` so the Medplum
 * subscription dispatches and the conversion handler runs again.
 *
 * Hidden in read-only sessions and when no parent DocumentReference id is
 * available (the patch target).
 *
 * @param props - Component props.
 * @param props.attachment - The attachment whose conversion failed.
 * @param props.documentReferenceId - Parent DocumentReference id; required to
 *   patch the right resource. Pass `undefined` to suppress the button.
 * @param props.size - Button size; defaults to `sm`.
 * @returns The button element, or `null` when gated out.
 */
export function RetryConversionButton({
  attachment,
  documentReferenceId,
  size = 'sm',
}: RetryConversionButtonProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const { retry, isRetrying } =
    useRetryAttachmentConversion(documentReferenceId)

  if (!documentReferenceId || isReadOnly) {
    return null
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      disabled={isRetrying}
      onClick={() => {
        // Why: fire-and-forget — the hook surfaces errors via toast and the
        // promise rejection is intentionally swallowed at the click handler
        // (we don't want unhandled-rejection warnings for an interaction
        // already reported to the user).
        void retry(attachment).catch(() => {})
      }}
    >
      <IconRefresh className="mr-1 size-3.5" />
      {isRetrying ? 'Retrying…' : 'Retry conversion'}
    </Button>
  )
}
